import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { modelsAPI } from '../services/api';

const SORT_OPTIONS = [
    { value: 'name_asc', label: 'Nome (A-Z)' },
    { value: 'name_desc', label: 'Nome (Z-A)' },
    { value: 'price_asc', label: 'Preco (Menor)' },
    { value: 'price_desc', label: 'Preco (Maior)' },
    { value: 'context_asc', label: 'Contexto (Menor)' },
    { value: 'context_desc', label: 'Contexto (Maior)' },
    { value: 'created_desc', label: 'Mais Recente' },
    { value: 'created_asc', label: 'Mais Antigo' },
];

function formatPrice(price) {
    const num = parseFloat(price);
    if (num === 0) return 'Gratis';
    if (num < 0.000001) return `$${(num * 1_000_000).toFixed(3)}/1M`;
    return `$${(num * 1_000_000).toFixed(2)}/1M`;
}

function formatContext(length) {
    if (!length) return '-';
    if (length >= 1_000_000) return `${(length / 1_000_000).toFixed(1)}M`;
    if (length >= 1_000) return `${(length / 1_000).toFixed(0)}K`;
    return length.toString();
}

function ModelSelector({ currentModel, onModelChange, onClose }) {
    const [models, setModels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('name_asc');
    const [selectedProvider, setSelectedProvider] = useState('all');
    const [selectedModality, setSelectedModality] = useState('all');
    const [showFree, setShowFree] = useState(false);
    const [selectedParams, setSelectedParams] = useState([]);
    const [expandedModel, setExpandedModel] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadModels();
    }, []);

    const loadModels = async () => {
        try {
            setLoading(true);
            setError(null);
            const { data } = await modelsAPI.getAll();
            if (data.success) {
                setModels(data.models);
            } else {
                setError(data.error);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Erro ao carregar modelos.');
        } finally {
            setLoading(false);
        }
    };

    // Extract unique values for filters
    const providers = useMemo(() => {
        const set = new Set(models.map(m => m.provider));
        return Array.from(set).sort();
    }, [models]);

    const modalities = useMemo(() => {
        const set = new Set(models.map(m => m.architecture.modality));
        return Array.from(set).sort();
    }, [models]);

    const allParams = useMemo(() => {
        const set = new Set();
        models.forEach(m => m.supported_parameters.forEach(p => set.add(p)));
        return Array.from(set).sort();
    }, [models]);

    // Filter and sort
    const filtered = useMemo(() => {
        let result = [...models];

        // Search
        if (search.trim()) {
            const q = search.toLowerCase();
            result = result.filter(m =>
                m.id.toLowerCase().includes(q) ||
                m.name.toLowerCase().includes(q) ||
                m.description.toLowerCase().includes(q) ||
                m.provider.toLowerCase().includes(q)
            );
        }

        // Provider
        if (selectedProvider !== 'all') {
            result = result.filter(m => m.provider === selectedProvider);
        }

        // Modality
        if (selectedModality !== 'all') {
            result = result.filter(m => m.architecture.modality === selectedModality);
        }

        // Free only
        if (showFree) {
            result = result.filter(m => parseFloat(m.pricing.prompt) === 0 && parseFloat(m.pricing.completion) === 0);
        }

        // Supported params filter
        if (selectedParams.length > 0) {
            result = result.filter(m =>
                selectedParams.every(p => m.supported_parameters.includes(p))
            );
        }

        // Sort
        const [field, dir] = sortBy.split('_');
        result.sort((a, b) => {
            let cmp = 0;
            switch (field) {
                case 'name':
                    cmp = a.name.localeCompare(b.name);
                    break;
                case 'price':
                    cmp = parseFloat(a.pricing.prompt) - parseFloat(b.pricing.prompt);
                    break;
                case 'context':
                    cmp = (a.context_length || 0) - (b.context_length || 0);
                    break;
                case 'created':
                    cmp = (a.created || 0) - (b.created || 0);
                    break;
                default:
                    cmp = 0;
            }
            return dir === 'desc' ? -cmp : cmp;
        });

        return result;
    }, [models, search, sortBy, selectedProvider, selectedModality, showFree, selectedParams]);

    // Group by provider
    const grouped = useMemo(() => {
        const groups = {};
        filtered.forEach(m => {
            if (!groups[m.provider]) groups[m.provider] = [];
            groups[m.provider].push(m);
        });
        return groups;
    }, [filtered]);

    const handleSelect = useCallback(async (modelId) => {
        try {
            setSaving(true);
            await modelsAPI.setCurrent(modelId);
            onModelChange(modelId);
        } catch (err) {
            setError('Erro ao salvar modelo.');
        } finally {
            setSaving(false);
        }
    }, [onModelChange]);

    const toggleParam = (param) => {
        setSelectedParams(prev =>
            prev.includes(param) ? prev.filter(p => p !== param) : [...prev, param]
        );
    };

    if (loading) {
        return (
            <div className="model-selector-overlay" onClick={onClose}>
                <div className="model-selector" onClick={e => e.stopPropagation()}>
                    <div className="model-selector-loading">
                        <div className="typing-dot"></div>
                        <div className="typing-dot"></div>
                        <div className="typing-dot"></div>
                        <p>Carregando modelos do OpenRouter...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="model-selector-overlay" onClick={onClose}>
            <div className="model-selector" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="ms-header">
                    <div className="ms-header-left">
                        <h3>Selecionar Modelo de IA</h3>
                        <span className="ms-count">{filtered.length} de {models.length} modelos com suporte a funcoes</span>
                    </div>
                    <button className="ms-close" onClick={onClose}>&times;</button>
                </div>

                {/* Current Model */}
                <div className="ms-current">
                    <span className="ms-current-label">Modelo atual:</span>
                    <span className="ms-current-value">{currentModel}</span>
                </div>

                {error && <div className="ms-error">{error}</div>}

                {/* Filters Bar */}
                <div className="ms-filters">
                    <div className="ms-search-row">
                        <input
                            type="text"
                            className="ms-search"
                            placeholder="Buscar modelo, provider, descricao..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            autoFocus
                        />
                        <select
                            className="ms-select"
                            value={sortBy}
                            onChange={e => setSortBy(e.target.value)}
                        >
                            {SORT_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="ms-filter-row">
                        <select
                            className="ms-select"
                            value={selectedProvider}
                            onChange={e => setSelectedProvider(e.target.value)}
                        >
                            <option value="all">Todos Providers ({providers.length})</option>
                            {providers.map(p => (
                                <option key={p} value={p}>{p}</option>
                            ))}
                        </select>

                        <select
                            className="ms-select"
                            value={selectedModality}
                            onChange={e => setSelectedModality(e.target.value)}
                        >
                            <option value="all">Todas Modalidades</option>
                            {modalities.map(m => (
                                <option key={m} value={m}>{m}</option>
                            ))}
                        </select>

                        <label className="ms-checkbox-label">
                            <input
                                type="checkbox"
                                checked={showFree}
                                onChange={e => setShowFree(e.target.checked)}
                            />
                            Apenas gratuitos
                        </label>
                    </div>

                    {/* Param tags */}
                    <div className="ms-params-filter">
                        <span className="ms-params-label">Filtrar parametros:</span>
                        <div className="ms-param-tags">
                            {allParams.map(p => (
                                <button
                                    key={p}
                                    className={`ms-param-tag ${selectedParams.includes(p) ? 'active' : ''}`}
                                    onClick={() => toggleParam(p)}
                                >
                                    {p}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Models List */}
                <div className="ms-models-list">
                    {Object.keys(grouped).length === 0 ? (
                        <div className="ms-empty">Nenhum modelo encontrado com os filtros aplicados.</div>
                    ) : (
                        Object.entries(grouped).map(([provider, providerModels]) => (
                            <div key={provider} className="ms-group">
                                <div className="ms-group-header">
                                    <span className="ms-group-name">{provider}</span>
                                    <span className="ms-group-count">{providerModels.length}</span>
                                </div>
                                {providerModels.map(model => (
                                    <div
                                        key={model.id}
                                        className={`ms-model-card ${model.id === currentModel ? 'active' : ''}`}
                                    >
                                        <div
                                            className="ms-model-main"
                                            onClick={() => setExpandedModel(expandedModel === model.id ? null : model.id)}
                                        >
                                            <div className="ms-model-info">
                                                <div className="ms-model-name">
                                                    {model.name}
                                                    {model.id === currentModel && <span className="ms-badge-active">ATIVO</span>}
                                                    {parseFloat(model.pricing.prompt) === 0 && parseFloat(model.pricing.completion) === 0 && (
                                                        <span className="ms-badge-free">FREE</span>
                                                    )}
                                                </div>
                                                <div className="ms-model-id">{model.id}</div>
                                            </div>
                                            <div className="ms-model-stats">
                                                <div className="ms-stat">
                                                    <span className="ms-stat-label">Input</span>
                                                    <span className="ms-stat-value">{formatPrice(model.pricing.prompt)}</span>
                                                </div>
                                                <div className="ms-stat">
                                                    <span className="ms-stat-label">Output</span>
                                                    <span className="ms-stat-value">{formatPrice(model.pricing.completion)}</span>
                                                </div>
                                                <div className="ms-stat">
                                                    <span className="ms-stat-label">Contexto</span>
                                                    <span className="ms-stat-value">{formatContext(model.context_length)}</span>
                                                </div>
                                                <div className="ms-stat">
                                                    <span className="ms-stat-label">Max Output</span>
                                                    <span className="ms-stat-value">{formatContext(model.top_provider.max_completion_tokens)}</span>
                                                </div>
                                            </div>
                                            <button
                                                className={`ms-select-btn ${model.id === currentModel ? 'selected' : ''}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSelect(model.id);
                                                }}
                                                disabled={model.id === currentModel || saving}
                                            >
                                                {model.id === currentModel ? 'Selecionado' : 'Usar'}
                                            </button>
                                        </div>

                                        {expandedModel === model.id && (
                                            <div className="ms-model-details">
                                                {model.description && (
                                                    <div className="ms-detail-section">
                                                        <span className="ms-detail-label">Descricao</span>
                                                        <p className="ms-detail-text">{model.description}</p>
                                                    </div>
                                                )}
                                                <div className="ms-detail-grid">
                                                    <div className="ms-detail-section">
                                                        <span className="ms-detail-label">Modalidade</span>
                                                        <p className="ms-detail-text">{model.architecture.modality}</p>
                                                    </div>
                                                    <div className="ms-detail-section">
                                                        <span className="ms-detail-label">Tokenizer</span>
                                                        <p className="ms-detail-text">{model.architecture.tokenizer}</p>
                                                    </div>
                                                    <div className="ms-detail-section">
                                                        <span className="ms-detail-label">Input</span>
                                                        <div className="ms-detail-tags">
                                                            {model.architecture.input_modalities.map(m => (
                                                                <span key={m} className="ms-modality-tag">{m}</span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <div className="ms-detail-section">
                                                        <span className="ms-detail-label">Output</span>
                                                        <div className="ms-detail-tags">
                                                            {model.architecture.output_modalities.map(m => (
                                                                <span key={m} className="ms-modality-tag">{m}</span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    {model.pricing.image && (
                                                        <div className="ms-detail-section">
                                                            <span className="ms-detail-label">Preco Imagem</span>
                                                            <p className="ms-detail-text">{formatPrice(model.pricing.image)}</p>
                                                        </div>
                                                    )}
                                                    <div className="ms-detail-section">
                                                        <span className="ms-detail-label">Moderado</span>
                                                        <p className="ms-detail-text">{model.top_provider.is_moderated ? 'Sim' : 'Nao'}</p>
                                                    </div>
                                                </div>
                                                <div className="ms-detail-section">
                                                    <span className="ms-detail-label">Parametros Suportados</span>
                                                    <div className="ms-detail-tags">
                                                        {model.supported_parameters.map(p => (
                                                            <span key={p} className="ms-param-badge">{p}</span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}

export default ModelSelector;
